<template>
  <div class="setup-step">
    <h3>{{ s__('ProjectSetup|Step 1: Basic Information') }}</h3>
    <p class="step-description">
      {{ s__('ProjectSetup|Set your project name and description.') }}
    </p>

    <div class="form-row">
      <label>{{ s__('ProjectSetup|Title') }}</label>
      <input
        v-model="title"
        type="text"
        :disabled="saving || !canUpdateTitle"
        :placeholder="s__('ProjectSetup|Enter project title')"
        class="input"
      />
      <div v-if="!canUpdateTitle" class="help-text warning">
        {{ s__('ProjectSetup|Only project owners can edit the title.') }}
      </div>
      <div v-else class="help-text">
        {{ s__('ProjectSetup|You can change this later in project settings.') }}
      </div>
    </div>

    <div class="form-row">
      <label>{{ s__('ProjectSetup|Description') }}</label>
      <textarea
        v-model="description"
        :disabled="saving || !canUpdateDescription"
        :placeholder="s__('ProjectSetup|Enter project description (optional)')"
        class="textarea"
        rows="4"
      />
      <div v-if="!canUpdateDescription" class="help-text warning">
        {{ s__('ProjectSetup|Only project owners can edit the description.') }}
      </div>
      <div v-else class="help-text">
        {{ s__('ProjectSetup|Optional description for your project.') }}
      </div>
    </div>

    <div class="form-row">
      <label>{{ s__('ProjectSetup|Code') }}</label>
      <input
        v-if="!codeLocked"
        v-model="code"
        type="text"
        :disabled="saving || !canSetCode"
        :placeholder="s__('ProjectSetup|Optional short code, e.g. TRCK')"
        class="input"
      />
      <input v-else :value="code" type="text" disabled class="input" />
      <div v-if="codeLocked" class="help-text warning">
        {{ s__('ProjectSetup|Project code is set and cannot be changed.') }}
      </div>
      <div v-else class="help-text">
        {{
          s__(
            'ProjectSetup|Optional. 3-12 chars, letters/digits/_/-. Must include at least one letter. Once set, code cannot be changed.'
          )
        }}
      </div>
    </div>

    <div v-if="saveError" class="error">{{ saveError }}</div>
    <div v-if="saveSuccess" class="success">
      {{ s__('ProjectSetup|Information saved successfully!') }}
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__ } from '@/locale'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  saving: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['save', 'skip'])

const title = ref('')
const description = ref('')
const code = ref('')
const originalTitle = ref('')
const originalDescription = ref('')
const originalCode = ref('')
const saveError = ref('')
const saveSuccess = ref(false)

const canUpdateTitle = computed(() => props.project?.canUpdateTitle ?? false)
const canUpdateDescription = computed(() => props.project?.canUpdateDescription ?? false)
const canSetCode = computed(() => (props.project?.canUpdateProject ?? false) && !originalCode.value)
const codeLocked = computed(() => !!originalCode.value)

const UPDATE_TITLE_MUTATION = gql`
  mutation UpdateProjectTitle($id: ID!, $title: String!) {
    updateProjectTitle(id: $id, title: $title) {
      project {
        id
        title
      }
      errors
    }
  }
`

const UPDATE_DESCRIPTION_MUTATION = gql`
  mutation UpdateProjectDescription($id: ID!, $description: String) {
    updateProjectDescription(id: $id, description: $description) {
      project {
        id
        description
      }
      errors
    }
  }
`

const UPDATE_PROJECT_CODE_MUTATION = gql`
  mutation UpdateProjectCode($id: ID!, $code: String) {
    updateProject(id: $id, code: $code) {
      project {
        id
        code
      }
      errors
    }
  }
`

const { mutate: updateTitle } = useMutation(UPDATE_TITLE_MUTATION)
const { mutate: updateDescription } = useMutation(UPDATE_DESCRIPTION_MUTATION)
const { mutate: updateProjectCode } = useMutation(UPDATE_PROJECT_CODE_MUTATION)

// Initialize from project
watch(
  () => props.project,
  projectData => {
    if (projectData) {
      title.value = projectData.title || ''
      description.value = projectData.description || ''
      code.value = projectData.code || ''
      originalTitle.value = projectData.title || ''
      originalDescription.value = projectData.description || ''
      originalCode.value = projectData.code || ''
    }
  },
  { immediate: true }
)

// Check if there are unsaved changes
function hasChanges() {
  const titleChanged = title.value !== originalTitle.value && canUpdateTitle.value
  const descriptionChanged =
    description.value !== originalDescription.value && canUpdateDescription.value
  const codeChanged = code.value !== originalCode.value && canSetCode.value
  return titleChanged || descriptionChanged || codeChanged
}

// Reset changes
function reset() {
  title.value = originalTitle.value
  description.value = originalDescription.value
  code.value = originalCode.value
  saveError.value = ''
  saveSuccess.value = false
}

// Expose methods to parent
defineExpose({
  hasChanges,
  reset,
  async save() {
    if (!props.project?.id) return

    saveError.value = ''
    saveSuccess.value = false

    try {
      const errors = []

      // Update title if changed and user has permission
      if (title.value !== originalTitle.value && canUpdateTitle.value) {
        const { data } = await updateTitle({
          id: props.project.id,
          title: title.value,
        })

        if (data?.updateProjectTitle?.errors?.length) {
          errors.push(...data.updateProjectTitle.errors)
        } else {
          originalTitle.value = title.value
        }
      }

      // Update description if changed and user has permission
      if (description.value !== originalDescription.value && canUpdateDescription.value) {
        const { data } = await updateDescription({
          id: props.project.id,
          description: description.value || undefined,
        })

        if (data?.updateProjectDescription?.errors?.length) {
          errors.push(...data.updateProjectDescription.errors)
        } else {
          originalDescription.value = description.value
        }
      }

      if (code.value !== originalCode.value && canSetCode.value) {
        const { data } = await updateProjectCode({
          id: props.project.id,
          code: code.value.trim() || null,
        })

        if (data?.updateProject?.errors?.length) {
          errors.push(...data.updateProject.errors)
        } else {
          originalCode.value = data?.updateProject?.project?.code || code.value.trim()
          code.value = originalCode.value
        }
      }

      if (errors.length > 0) {
        throw new Error(errors.join(', '))
      }

      saveSuccess.value = true
      // Return saved data instead of emitting event - parent will handle step advancement
      return { title: title.value, description: description.value, code: code.value }
    } catch (e) {
      saveError.value = e.message || 'Failed to update project'
      return false
    }
  },
})
</script>

<style scoped>
.setup-step {
  max-width: 600px;
}

.setup-step h3 {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.form-row {
  margin-bottom: 16px;
}

.form-row label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
}

.input,
.textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
}

.textarea {
  resize: vertical;
}

.input:disabled,
.textarea:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.help-text {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

.help-text.warning {
  color: #f57c00;
  font-weight: 500;
}

.error {
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
}

.success {
  color: #2e7d32;
  font-size: 14px;
  margin-top: 8px;
}
</style>
